import { Partition } from '../partition';
import {
  MetricCollector,
  type MetricsMap,
  type BaseLogParam,
  type Dimensions,
  reservoirSample,
  computeMetricKey,
} from '@snapchat/metrics-utils';

// TODO: See if we can use '@snapchat/ts-protos/graphene/metrics', but they
// use `string` for uint64 encoding.
import {
  MetricFrame,
  Metric,
  AppVersion,
  type AppVersion_VersionNumber,
  OsType,
} from '../generated/proto/metrics_web';

export interface WebMetricCollectorOptions {
  // build flavor, e.g., local, stage, prod
  flavor?: string;
  // Max size per metric
  maxSizePerMetric?: number;
  // use project name
  partitionName: Partition;
  // SNAPSHOT, OG, MUSHROOM see Blizzard Schema's AppVariantType https://snapchat.quip.com/UVHDA0J59IDz
  variant?: string;
  // Sematic version of the app
  versionNumber?: AppVersion_VersionNumber;
  // dimensions that'll be added to each metric.
  defaultDimensions?: Dimensions;
}

type WebMetricsMap = MetricsMap<Metric>;

/** Dedicated for frontend use case. Collect and process metrics. */
export class WebMetricCollector extends MetricCollector<Metric> {
  partitionName: Partition;
  private flavor?: string;
  private variant?: string;
  private versionNumber?: AppVersion_VersionNumber;
  private maxSizePerMetric: number;
  private defaultDimensions?: Dimensions;

  constructor(options: WebMetricCollectorOptions) {
    super();
    if (
      !options.partitionName ||
      !Partition[options.partitionName.toUpperCase() as keyof typeof Partition]
    ) {
      throw new Error('Partition is not defined!');
    }
    this.partitionName = options.partitionName;
    this.flavor = options.flavor;
    this.variant = options.variant;
    this.versionNumber = options.versionNumber;
    this.maxSizePerMetric = options.maxSizePerMetric || 50;
    this.defaultDimensions = options.defaultDimensions;
  }

  increment({ metricsName, dimensions, value }: BaseLogParam & { value: number }): void {
    const metricKey = computeMetricKey({ metricsName, dimensions });
    const prevMetricObject = this.metrics.counters[metricKey];
    this.metrics.counters[metricKey] = this.updateMetricObject({
      prevMetricObject,
      metricsName,
      dimensions: this._decorateDimensions(dimensions),
      value,
      shouldAggregate: true,
    });
  }

  addHistogram({ metricsName, dimensions, value }: BaseLogParam & { value: number }): void {
    const metricKey = computeMetricKey({ metricsName, dimensions });
    const prevMetricObject = this.metrics.levels[metricKey];
    this.metrics.levels[metricKey] = this.updateMetricObject({
      prevMetricObject,
      metricsName,
      dimensions: this._decorateDimensions(dimensions),
      value,
      shouldAggregate: false,
    });
  }

  addTimer({ metricsName, dimensions, milliSec }: BaseLogParam & { milliSec: number }): void {
    const metricKey = computeMetricKey({ metricsName, dimensions });
    const prevMetricObject = this.metrics.timers[metricKey];
    this.metrics.timers[metricKey] = this.updateMetricObject({
      prevMetricObject,
      metricsName,
      dimensions: this._decorateDimensions(dimensions),
      value: milliSec,
      shouldAggregate: false,
    });
  }

  getPayload(): Uint8Array {
    const frame = this.convertToMetricFrame(this.metrics);
    return MetricFrame.encode(frame).finish();
  }

  updateMetricObject({
    prevMetricObject,
    metricsName,
    dimensions = {},
    value,
    shouldAggregate = false,
  }: BaseLogParam & {
    prevMetricObject: Metric | undefined;
    value: number;
    shouldAggregate?: boolean;
  }): Metric {
    let metricObject = prevMetricObject;
    // If there was no metric object, then create a new one
    if (!metricObject) {
      metricObject = Metric.fromPartial({
        partitionName: this.partitionName,
        metricName: metricsName,
        customDimensions: dimensions,
      });
    }

    // if aggregate, then sum up the value into single value
    // otherwise, append new metric in the value list
    if (shouldAggregate) {
      const existingValueList = metricObject.values;
      const newValue = value + (Number(existingValueList[0]) || 0);
      metricObject.values = [newValue];
    } else {
      metricObject.values.push(value);
    }
    return metricObject;
  }

  // convert metrics into MetricFrame which is protobuf format
  convertToMetricFrame(metricsToConvert: WebMetricsMap): MetricFrame {
    const frame = MetricFrame.create();
    frame.clientOsType = OsType.OS_WEB;

    // If app version related values are set, then set app version
    if (this.flavor || this.variant || this.versionNumber) {
      const appVersion = AppVersion.create();
      if (this.flavor) {
        appVersion.flavor = this.flavor;
      }
      if (this.variant) {
        appVersion.variant = this.variant;
      }
      if (this.versionNumber) {
        appVersion.versionNumber = this.versionNumber;
      }
      frame.appVersion = appVersion;
    }

    Object.keys(metricsToConvert.counters).forEach(key => {
      const metric = metricsToConvert.counters[key];
      frame.counters.push(metric);
    });
    Object.keys(metricsToConvert.timers).forEach(key => {
      const metric = this.sampleMetricValues(metricsToConvert.timers[key]);
      frame.timers.push(metric);
    });
    Object.keys(metricsToConvert.levels).forEach(key => {
      const metric = this.sampleMetricValues(metricsToConvert.levels[key]);
      frame.levels.push(metric);
    });
    return frame;
  }

  // Reservoir Sampling on metric value list
  sampleMetricValues(metricObject: Metric): Metric {
    metricObject.values = reservoirSample(metricObject.values, this.maxSizePerMetric);
    return metricObject;
  }

  _decorateDimensions(dimensions: Dimensions | undefined): Dimensions | undefined {
    if (!this.defaultDimensions) {
      return dimensions;
    }
    return {
      ...this.defaultDimensions,
      ...(dimensions ?? {}),
    };
  }
}
